import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Mobile-only brand header for Login/Signup — a compact version of the app's
// boot-splash blueprint motif (reuses the same arcdxBlueprint* keyframes from
// styles.css) so the two screens feel like one continuous brand moment.
const AuthBlueprintHero = ({ tagline = 'Interior Design Management' }) => {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #081b3a 0%, #0d2b5c 55%, #123a75 100%)',
        pt: 'max(28px, env(safe-area-inset-top))',
        pb: 3,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '-42px',
          backgroundImage:
            'linear-gradient(rgba(120,170,255,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(120,170,255,0.09) 1px, transparent 1px)',
          backgroundSize: '42px 42px',
          animation: 'arcdxBlueprintGrid 9s linear infinite',
        }}
      />

      <Box sx={{ position: 'relative', width: 108, height: 108, mx: 'auto' }}>
        <svg viewBox="0 0 220 220" width="108" height="108" style={{ overflow: 'visible' }}>
          <circle
            cx="74"
            cy="121"
            r="26"
            fill="url(#arcdxAuthGlow)"
            style={{ opacity: 0, animation: 'arcdxBlueprintGlow 0.7s ease forwards 1.2s, arcdxBlueprintGlowPulse 2.4s ease-in-out infinite 1.9s' }}
          />
          <defs>
            <radialGradient id="arcdxAuthGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffd580" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#ffd580" stopOpacity="0" />
            </radialGradient>
          </defs>

          <path
            d="M50,170 L50,95 L110,45 L170,95 L170,170 Z"
            fill="#4db2fc"
            style={{ opacity: 0, animation: 'arcdxBlueprintFill 0.6s ease forwards 0.85s' }}
          />

          <path
            d="M50,170 L50,95 L110,45 L170,95 L170,170 L50,170 Z"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="1"
            style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 'arcdxBlueprintDraw 0.75s ease forwards 0.05s' }}
          />

          <path
            d="M96,170 L96,124 L132,124 L132,170"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="1"
            style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 'arcdxBlueprintDraw 0.4s ease forwards 0.85s' }}
          />

          <rect
            x="61"
            y="108"
            width="26"
            height="26"
            rx="2"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="4"
            pathLength="1"
            style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 'arcdxBlueprintDraw 0.35s ease forwards 1.05s' }}
          />
        </svg>
      </Box>

      <Typography
        sx={{
          fontSize: '24px',
          fontWeight: 800,
          color: '#fff',
          fontFamily: 'Roboto, sans-serif',
          letterSpacing: '0.12em',
          mt: 1,
          opacity: 0,
          animation: 'arcdxBlueprintWordUp 0.6s ease forwards 1.3s',
        }}
      >
        DigiBooks
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: 'rgba(234,244,255,0.75)',
          fontFamily: 'Roboto, sans-serif',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mt: 0.5,
          opacity: 0,
          animation: 'arcdxBlueprintTagline 0.6s ease forwards 1.55s',
        }}
      >
        {tagline}
      </Typography>
    </Box>
  );
};

// Desktop-only (md+) full-height vertical brand panel — the split-card left column
// for Login/Signup/SubUserFirstLogin. Same blueprint motif as the mobile hero,
// scaled up. Gradient defs use a DIFFERENT id (arcdxPanelGlow) so both components
// can mount without duplicate-id conflicts.
export const AuthBlueprintPanel = ({ tagline = 'Interior Design Management' }) => {
  const features = [
    'Clients, vendors & payments in one place',
    'Approval workflows that keep budgets honest',
    'Financial clarity for every project',
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #081b3a 0%, #0d2b5c 55%, #123a75 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: '48px 40px',
        height: '100%',
        minHeight: 560,
        borderRadius: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '-42px',
          backgroundImage:
            'linear-gradient(rgba(120,170,255,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(120,170,255,0.09) 1px, transparent 1px)',
          backgroundSize: '42px 42px',
          animation: 'arcdxBlueprintGrid 9s linear infinite',
        }}
      />

      <Box sx={{ position: 'relative', width: 180, height: 180 }}>
        <svg viewBox="0 0 220 220" width="180" height="180" style={{ overflow: 'visible' }}>
          <circle
            cx="74"
            cy="121"
            r="26"
            fill="url(#arcdxPanelGlow)"
            style={{ opacity: 0, animation: 'arcdxBlueprintGlow 0.7s ease forwards 1.2s, arcdxBlueprintGlowPulse 2.4s ease-in-out infinite 1.9s' }}
          />
          <defs>
            <radialGradient id="arcdxPanelGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffd580" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#ffd580" stopOpacity="0" />
            </radialGradient>
          </defs>

          <path
            d="M50,170 L50,95 L110,45 L170,95 L170,170 Z"
            fill="#4db2fc"
            style={{ opacity: 0, animation: 'arcdxBlueprintFill 0.6s ease forwards 0.85s' }}
          />

          <path
            d="M50,170 L50,95 L110,45 L170,95 L170,170 L50,170 Z"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="1"
            style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 'arcdxBlueprintDraw 0.75s ease forwards 0.05s' }}
          />

          <path
            d="M96,170 L96,124 L132,124 L132,170"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="1"
            style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 'arcdxBlueprintDraw 0.4s ease forwards 0.85s' }}
          />

          <rect
            x="61"
            y="108"
            width="26"
            height="26"
            rx="2"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="4"
            pathLength="1"
            style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 'arcdxBlueprintDraw 0.35s ease forwards 1.05s' }}
          />
        </svg>
      </Box>

      <Typography
        sx={{
          position: 'relative',
          fontSize: '40px',
          fontWeight: 800,
          color: '#fff',
          fontFamily: 'Roboto, sans-serif',
          letterSpacing: '0.14em',
          mt: 1.5,
          opacity: 0,
          animation: 'arcdxBlueprintWordUp 0.6s ease forwards 1.3s',
        }}
      >
        DigiBooks
      </Typography>
      <Typography
        sx={{
          position: 'relative',
          fontSize: '12px',
          fontWeight: 600,
          color: 'rgba(234,244,255,0.75)',
          fontFamily: 'Roboto, sans-serif',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mt: 0.75,
          opacity: 0,
          animation: 'arcdxBlueprintTagline 0.6s ease forwards 1.55s',
        }}
      >
        {tagline}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          mt: 4.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          opacity: 0,
          animation: 'arcdxBlueprintTagline 0.6s ease forwards 1.8s',
        }}
      >
        {features.map((feature) => (
          <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: '#4db2fc',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'rgba(234,244,255,0.75)',
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.02em',
              }}
            >
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AuthBlueprintHero;
